import { Clock, MessageSquarePlus, Globe } from 'lucide-react'
import type { Visitor } from './types'
import { cn } from '@/lib/utils'
import { formatTimeAgo } from '@/lib/dateUtils'
import { VisitorAvatar } from '@/components/ui/VisitorAvatar'
import { CopyableText } from '@/components/ui/CopyableText'
import { Tooltip } from '@/components/ui/Tooltip'

// Extract displayable page info from current_page
function getPageDisplay(currentPage: Visitor['current_page']): string | null {
  if (!currentPage) return null
  if (currentPage.title) return currentPage.title
  // Fall back to URL path if no title
  try {
    const url = new URL(currentPage.url)
    return url.pathname === '/' ? url.hostname : url.pathname
  } catch {
    return currentPage.url
  }
}

interface VisitorListItemProps {
  visitor: Visitor
  isSelected: boolean
  onClick: () => void
  onHover?: () => void
  onHoverEnd?: () => void
  onStartConversation?: () => void
}

export function VisitorListItem({ visitor, isSelected, onClick, onHover, onHoverEnd, onStartConversation }: VisitorListItemProps) {
  const handleStartConversation = (e: React.MouseEvent) => {
    e.stopPropagation()
    onStartConversation?.()
  }

  return (
    <button
      data-visitor-id={visitor.id}
      onClick={onClick}
      onMouseEnter={onHover}
      onMouseLeave={onHoverEnd}
      className={cn(
        "w-full flex flex-col items-start gap-2 border-b border-sidebar-secondary-accent/30 p-4 text-sm leading-tight last:border-b-0 cursor-pointer text-left group",
        "hover:bg-sidebar-secondary-accent/50",
        isSelected && "bg-sidebar-secondary-accent/70",
        !visitor.location && "opacity-50"
      )}
    >
      <div className="flex w-full items-center gap-2">
        <VisitorAvatar
          name={visitor.name}
          identified={visitor.identified}
          active={visitor.active}
          live={visitor.live}
          countryCode={visitor.location?.country_code}
        />

        <span className="truncate font-medium text-sidebar-secondary-foreground">
          {visitor.name}
        </span>

        {/* Start conversation button - shown on hover */}
        <Tooltip content="Start conversation" side="left">
          <button
            type="button"
            onClick={handleStartConversation}
            className="shrink-0 p-1.5 rounded hover:bg-primary/20 transition-colors cursor-pointer opacity-0 group-hover:opacity-100 ml-auto"
          >
            <MessageSquarePlus className="size-4 text-primary" />
          </button>
        </Tooltip>

        {visitor.last_seen_at && (
          <span className="text-xs text-sidebar-secondary-muted flex items-center gap-1 group-hover:hidden">
            <Clock className="size-3" />
            {formatTimeAgo(visitor.last_seen_at)}
          </span>
        )}
      </div>

      {/* Location */}
      {visitor.location && (
        <span className="text-xs text-sidebar-secondary-muted">
          {[visitor.location.city, visitor.location.country_code].filter(Boolean).join(', ')}
        </span>
      )}

      {/* Email */}
      {visitor.email && (
        <CopyableText
          text={visitor.email}
          className="text-xs text-sidebar-secondary-muted w-full"
          toastMessage="Email copied to clipboard"
        />
      )}

      {/* Current page */}
      {visitor.current_page && (
        <Tooltip content={visitor.current_page.url} side="right">
          <a
            href={visitor.current_page.url}
            target="_blank"
            rel="noopener noreferrer"
            onClick={(e) => e.stopPropagation()}
            className="text-xs text-sidebar-secondary-muted flex items-center gap-1 w-full hover:text-primary transition-colors cursor-pointer"
          >
            <Globe className="size-3 shrink-0" />
            <span className="truncate">{getPageDisplay(visitor.current_page)}</span>
          </a>
        </Tooltip>
      )}
    </button>
  )
}
